import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 8: Kubernetes-Native Time-Series Forecasting Platform
 * NeuralProphet + PostgreSQL + K8S MLOps
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 6,
    fontWeight: 800,
    fontSize: 12,
    color: "#0f172a",
    width: 220,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0b2e59",
    width: 140,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#4a0b2a",
    width: 140,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0f3d1f",
    width: 160,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#92400e",
    width: 180,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#05333b",
    width: 140,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project8FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background groups (stacked columns)
      {
        id: "bg-online",
        position: { x: 60, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 590,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-async",
        position: { x: 640, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 560,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-mlops",
        position: { x: 1220, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 610,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-obs",
        position: { x: 1800, y: 40 },
        data: { label: "" },
        style: {
          width: 460,
          height: 560,
          background: "#f5f3ff40",
          border: "3px solid #c4b5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Section titles
      { id: "sec-online", position: { x: 80, y: 50 }, data: { label: "Online Forecast" }, style: nodeStyle.section },
      { id: "sec-async", position: { x: 660, y: 50 }, data: { label: "Async Forecast" }, style: nodeStyle.section },
      { id: "sec-mlops", position: { x: 1240, y: 50 }, data: { label: "Training + Promotion" }, style: nodeStyle.section },
      { id: "sec-obs", position: { x: 1820, y: 50 }, data: { label: "Observability" }, style: nodeStyle.section },

      // Online path (vertical)
      { id: "n1", position: { x: 190, y: 130 }, data: { label: "Client/App" }, style: nodeStyle.compute },
      { id: "n2", position: { x: 190, y: 220 }, data: { label: "Ingress" }, style: nodeStyle.net },
      { id: "n3", position: { x: 190, y: 310 }, data: { label: "K8S forecast-api" }, style: nodeStyle.ops },
      { id: "n4", position: { x: 190, y: 400 }, data: { label: "Redis Cache" }, style: nodeStyle.data },
      { id: "n5", position: { x: 190, y: 490 }, data: { label: "PgBouncer" }, style: nodeStyle.net },
      { id: "n6", position: { x: 190, y: 580 }, data: { label: "PostgreSQL" }, style: nodeStyle.data },
      { id: "n13", position: { x: 0, y: 280 }, data: { label: "Active Model Alias" }, style: { ...nodeStyle.note, width: 140, padding: 4 } },
      { id: "n9", position: { x: 0, y: 400 }, data: { label: "Model Registry" }, style: nodeStyle.data },

      // Async path (vertical)
      { id: "n7", position: { x: 590, y: 150 }, data: { label: "Queue" }, style: { ...nodeStyle.net, width: 120, padding: 4, fontSize: 11 } },
      { id: "n8", position: { x: 950, y: 120 }, data: { label: "K8S forecast-worker" }, style: { ...nodeStyle.ops, width: 135, padding: 4, fontSize: 11 } },
      { id: "n5a", position: { x: 700, y: 450 }, data: { label: "PgBouncer" }, style: nodeStyle.net },
      { id: "n6a", position: { x: 700, y: 550 }, data: { label: "PostgreSQL" }, style: nodeStyle.data },
      { id: "n13a", position: { x: 600, y: 310 }, data: { label: "Active Model Alias" }, style: { ...nodeStyle.note, width: 140, padding: 4 } },
      { id: "n9a", position: { x: 900, y: 550 }, data: { label: "Model Registry" }, style: nodeStyle.data },

      // MLOps (vertical)
      { id: "n10", position: { x: 1280, y: 150 }, data: { label: "trainer Job" }, style: nodeStyle.ops },
      { id: "n11", position: { x: 1280, y: 240 }, data: { label: "evaluator Job" }, style: nodeStyle.ops },
      { id: "n12", position: { x: 1280, y: 330 }, data: { label: "promoter Job" }, style: nodeStyle.ops },
      { id: "n6b", position: { x: 1280, y: 420 }, data: { label: "PostgreSQL" }, style: nodeStyle.data },
      { id: "n9b", position: { x: 1280, y: 510 }, data: { label: "Model Registry" }, style: nodeStyle.data },
      { id: "n13b", position: { x: 1280, y: 600 }, data: { label: "Active Alias" }, style: { ...nodeStyle.note, width: 130, padding: 4 } },

      // Observability (vertical)
      { id: "n14", position: { x: 1860, y: 160 }, data: { label: "Monitoring" }, style: nodeStyle.ops },
      { id: "n14a", position: { x: 1860, y: 250 }, data: { label: "Dashboards/Alerts" }, style: nodeStyle.ops },
      { id: "n14b", position: { x: 1860, y: 340 }, data: { label: "Drift/Triggers" }, style: nodeStyle.ops },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const orange = { stroke: "#f59e0b" };
    const purple = { stroke: "#7c3aed" };

    return [
      // Online path (vertical)
      mkEdge("e1", "n1", "n2", "GET /forecast", { style: { ...blue } }),
      mkEdge("e2", "n2", "n3", "route", { style: { ...blue } }),
      mkEdge("e3", "n3", "n4", "cache check", { style: { ...blue } }),
      mkEdge("e4", "n4", "n5", "cache miss", { style: { ...blue } }),
      mkEdge("e5", "n5", "n6", "SQL read", { style: { ...blue } }),
      mkEdge("e6", "n3", "n13", "read alias", { style: { ...blue } }),
      mkEdge("e7", "n3", "n9", "load model", { style: { ...blue } }),
      mkEdge("e8", "n3", "n1", "response", { style: { ...blue } }),

      // Async path (vertical)
      mkEdge("e9", "n7", "n8", "consume", { style: { ...green, strokeDasharray: "10 8" } }),
      mkEdge("e10", "n8", "n5a", "fetch data", { style: { ...green } }),
      mkEdge("e11", "n5a", "n6a", "SQL read", { style: { ...green } }),
      mkEdge("e12", "n8", "n13a", "read alias", { style: { ...green } }),
      mkEdge("e13", "n8", "n9a", "load model", { style: { ...green } }),
      mkEdge("e14", "n8", "n7", "job complete", { style: { ...green, strokeDasharray: "10 8" } }),

      // MLOps (vertical)
      mkEdge("e15", "n10", "n6b", "read training", { style: { ...orange } }),
      mkEdge("e16", "n10", "n9b", "save model", { style: { ...orange } }),
      mkEdge("e17", "n10", "n11", "eval", { style: { ...orange } }),
      mkEdge("e18", "n11", "n12", "promote", { style: { ...orange } }),
      mkEdge("e19", "n12", "n13b", "update alias", { style: { ...orange } }),
      mkEdge("e20", "n12", "n3", "notify API", { style: { ...orange, strokeDasharray: "10 8" } }),
      mkEdge("e21", "n12", "n8", "notify workers", { style: { ...orange, strokeDasharray: "10 8" } }),

      // Observability (vertical)
      mkEdge("e22", "n14", "n14a", "dashboards", { style: { ...purple } }),
      mkEdge("e23", "n14", "n14b", "drift alerts", { style: { ...purple } }),
      mkEdge("e24", "n3", "n14", "metrics", { style: { ...purple } }),
      mkEdge("e25", "n8", "n14", "metrics", { style: { ...purple } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "700px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.35}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
